import Coordinate from 'jsts/org/locationtech/jts/geom/Coordinate';
import { DrawingMode } from 'src/components/viewers/SheetsViewerV2/autodesk/tools/ShapesDrawingTool';
import { mapPointsToMultiPolygons } from 'src/components/viewers/SheetsViewerV2/utils/shapes';
import {
  LineStringShapeDrawingResult,
  PolygonShapeDrawingResult,
} from 'src/components/orderContractor/pdf-shapes/SheetShapeDrawing';
import {
  LinearRing2,
  MultiPolygon2,
  Point2,
  Polygon2,
} from 'src/domain/geometry/geometric-types';
import { getSheetShapeUrn } from 'src/components/viewers/common/hooks/useSheetShapesManager';
import { CreateSheetShapeInput } from 'src/gql/graphql';
import { ApiPoint } from 'src/components/viewers/SheetsViewerV2/utils/apiCoordinatesConverter';
import { ShapeDrawingResultType } from 'src/components/orderContractor/pdf-shapes/SheetShapeDrawing';

type ShapesPoints = ApiPoint[][];

// eslint-disable-next-line complexity
export function mapDrawingResults(
  drawingMode: DrawingMode,
  points: ShapesPoints,
  wedgeData?: { diamonds: any[]; kileTypes: any[] }
) {
  switch (drawingMode) {
    case DrawingMode.Polygon:
    case DrawingMode.MagicPolygon:
    case DrawingMode.Rectangle:
    case DrawingMode.RoofOutline:
      return mapMultiPolygons(points);
    case DrawingMode.Polyline:
      return mapPolyLines(points);
    case DrawingMode.Wedges:
      return [
        {
          type: ShapeDrawingResultType.Wedge,
          points: (points[0] || []).map(mapApiPointToPoint2),
          valid: (points[0]?.length || 0) >= 2,
          wedgeData,
        },
      ];
    case DrawingMode.DrainLine:
      return mapDrainLines(points);
    case DrawingMode.Point:
      return mapPoints(points);
  }
}

function mapMultiPolygons(points: ShapesPoints): PolygonShapeDrawingResult {
  try {
    const multiPolygon = mapPointsToMultiPolygons(points);
    const polygonsCount = multiPolygon.getNumGeometries();
    const polygons: Polygon2[] = [];

    for (let i = 0; i < polygonsCount; i++) {
      const polygon = multiPolygon.getGeometryN(i);
      const exterior = polygon
        .getExteriorRing()
        .getCoordinates()
        .map(mapCoordinateToPoint2);
      const interiors: LinearRing2[] = [];

      for (let j = 0; j < polygon.getNumInteriorRing(); j++) {
        interiors.push(
          polygon
            .getInteriorRingN(j)
            .getCoordinates()
            .map(mapCoordinateToPoint2)
        );
      }

      if (polygon.getGeometryType() === 'Polygon') {
        polygons.push({ exterior, interiors });
      }
    }

    return { valid: true, multipolygon: { polygons } };
  } catch (e) {
    console.error(e);
    return { valid: false };
  }
}

function mapPolyLines(points: ShapesPoints): LineStringShapeDrawingResult[] {
  return points.map((points) => ({
    points: points.map(mapApiPointToPoint2),
    valid: points.length >= 2,
  }));
}

function mapDrainLines(points: ShapesPoints): LineStringShapeDrawingResult[] {
  // For drain lines, we want all points in a single result, not separate results
  // This matches how TaperedInsulationShape expects the data
  if (points.length === 0) {
    return [];
  }

  // Flatten all points from all arrays into a single array
  const allPoints = points.flatMap((pointArray) =>
    pointArray.map(mapApiPointToPoint2)
  );

  return [
    {
      points: allPoints,
      valid: allPoints.length >= 2,
    },
  ];
}

function mapPoints(points: ShapesPoints): LineStringShapeDrawingResult[] {
  return points.map((singlePoint) => ({
    points: singlePoint.map(mapApiPointToPoint2),
    valid: singlePoint.length === 1,
  }));
}

function mapWedges(
  points: ShapesPoints,
  wedgeData?: { diamonds: any[]; kileTypes: any[] }
): any[] {
  return points.map((points) => ({
    points: points.map(mapApiPointToPoint2),
    valid: points.length >= 2,
    type: 'Wedge',
    diamond: wedgeData?.diamonds || [],
    kileType: wedgeData?.kileTypes || [],
  }));
}

function mapCoordinateToPoint2(coordinate: Coordinate): Point2 {
  return [coordinate.x, coordinate.y] as Point2;
}

function mapApiPointToPoint2(vector3: ApiPoint): Point2 {
  return [vector3.x, vector3.y] as Point2;
}

function getCreateCommonInput(
  projectId: string,
  sheetId: string,
  sheetPageNumber: number,
  shapeName: string,
  folder: string | null
) {
  return {
    projectId,
    sheetId,
    sheetPageNumber,
    name: shapeName,
    urn: getSheetShapeUrn(projectId),
    folder,
  };
}

export function getCreatePolygonInput(
  projectId: string,
  sheetId: string,
  sheetPageNumber: number,
  shapeName: string,
  multipolygon: MultiPolygon2,
  selectedFolder: string | null
): CreateSheetShapeInput {
  return {
    sheetShape: {
      ...getCreateCommonInput(
        projectId,
        sheetId,
        sheetPageNumber,
        shapeName,
        selectedFolder
      ),
      sheetShapePolygon: {
        create: [
          {
            multipolygon: {
              polygons: multipolygon.polygons.map((polygon) => ({
                exterior: {
                  points: polygon.exterior.map((point) => ({
                    x: point[0],
                    y: point[1],
                  })),
                },
                interiors: polygon.interiors.map((interior) => ({
                  points: interior.map((point) => ({
                    x: point[0],
                    y: point[1],
                  })),
                })),
              })),
            },
          },
        ],
      },
    },
  };
}

export function getCreateLineInput(
  projectId: string,
  sheetId: string,
  sheetPageNumber: number,
  shapeName: string,
  points: Point2[],
  selectedFolder: string | null
): CreateSheetShapeInput {
  return {
    sheetShape: {
      ...getCreateCommonInput(
        projectId,
        sheetId,
        sheetPageNumber,
        shapeName,
        selectedFolder
      ),
      sheetShapeLine: {
        create: [
          {
            points: points.map((point) => ({
              x: point[0],
              y: point[1],
            })),
          },
        ],
      },
    },
  };
}

export function getCreateDrainLineInput(
  projectId: string,
  sheetId: string,
  sheetPageNumber: number,
  shapeName: string,
  points: Point2[],
  selectedFolder: string | null
): CreateSheetShapeInput {
  return {
    sheetShape: {
      ...getCreateCommonInput(
        projectId,
        sheetId,
        sheetPageNumber,
        shapeName,
        selectedFolder
      ),
      sheetShapeSplitLine: {
        create: [
          {
            points: points.map((point) => ({
              x: point[0],
              y: point[1],
            })),
          },
        ],
      },
    },
  };
}

export function getCreatePointInput(
  projectId: string,
  sheetId: string,
  sheetPageNumber: number,
  shapeName: string,
  points: Point2[],
  selectedFolder: string | null
): CreateSheetShapeInput[] {
  return points.map((point) => {
    return {
      sheetShape: {
        ...getCreateCommonInput(
          projectId,
          sheetId,
          sheetPageNumber,
          shapeName,
          selectedFolder
        ),
        sheetShapePoint: {
          create: [
            {
              point: {
                x: point[0],
                y: point[1],
              },
            },
          ],
        },
      },
    };
  });
}
